import { useCallback, useEffect, useState } from "react";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import OwButton from "@components/shared/OwButton/OwButton";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import { PaginationBar } from "@components/shared/PaginationBar/PaginationBar";
import { TypeaheadFn } from "@components/shared/TypeaheadFn/TypeaheadFn";
import { BulkGenerateSerialsSidebar } from "@components/shared/BulkGenerateSerialsSidebar/BulkGenerateSerialsSidebar";
import IntegrationsConnectedBar from "@components/shared/IntegrationsConnectedBar/IntegrationsConnectedBar";
import { useAppContextNonNull } from "@contexts/AppContext";
import SerialService from "@services/SerialService";
import ISerial from "@models/ISerial";
import IOrder from "@models/IOrder";
import IOrder_ItemUI from "@models/IOrder_ItemUI";
import IGetSerialsQuery from "@models/DTO/IGetSerialsQuery";
import IBulkAssignSerialsDTO from "@models/DTO/IBulkAssignSerialsDTO";
import { useVendorSourceTypeahead } from "@hooks/useVendorSourceTypeahead";

type Props = {
    order: IOrder;
    row: IOrder_ItemUI;
    cancel: (refresh?: boolean) => void;
};

export const BulkAddSerialSidebar = (props: Props) => {
    const { userModel, supplierModel, vendorModel } = useAppContextNonNull();

    // Tab state
    const [tab, setTab] = useState<"existing" | "generate">("existing");

    // View state (for existing tab)
    const [view, setView] = useState<"select" | "review">("select");

    // Data
    const [serials, setSerials] = useState<ISerial[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    // Selection
    const [selectedSerials, setSelectedSerials] = useState<Map<number, ISerial>>(new Map());

    // Filter
    const [filterValue, setFilterValue] = useState<string>("");

    // Pagination
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(25);
    const [hasPaginationChange, setHasPaginationChange] = useState<boolean>(false);

    // Status
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    const {
        vendorSourceOptions,
        selectedVendorSource,
        resetVendorSource,
        lookupVendorSource,
        selectVendorSource,
        clearVendorSource,
        resetVendorSourceFn,
    } = useVendorSourceTypeahead(userModel, supplierModel);

    // Computed
    const alreadyAssignedCount = (props.row.Serials || []).length;
    const maxSerials = props.row.Catalog_Item?.max_serials_per_order_item;
    const remainingSlots = maxSerials ? maxSerials - alreadyAssignedCount : Infinity;
    const selectedCount = selectedSerials.size;
    const isAtMax = maxSerials ? selectedCount >= remainingSlots : false;

    const fetchSerials = useCallback(async () => {
        try {
            setIsLoading(true);

            const query: IGetSerialsQuery = {
                unassigned: "true",
                limit: limit.toString(),
                page: offset.toString(),
                order_by: "value",
                order_dir: "ASC",
                fastbound_status: "active,null",
                fastbound_account_id: props.order.Vendor?.Vendor_Integration?.fastbound_account_id?.toString(),
            };

            if (props.row.Catalog_Item?.id) {
                query.catalog_item_id = props.row.Catalog_Item.id.toString();
            }

            if (filterValue.trim()) {
                query.serial = filterValue.trim();
            }

            if (selectedVendorSource) {
                query.vendor_source_id = selectedVendorSource.id.toString();
            }

            const data = await SerialService.getSerials(query, userModel, supplierModel, vendorModel);
            setSerials(data.serials || []);
            setTotalCount(data.totalCount || 0);
        } catch (error) {
            console.error("Error fetching serials for bulk add:", error);
            setSerials([]);
            setTotalCount(0);
        } finally {
            setIsLoading(false);
        }
    }, [userModel, supplierModel, vendorModel, offset, limit, filterValue, selectedVendorSource, props.row.Catalog_Item?.id]);

    useEffect(() => {
        fetchSerials();
    }, []);

    useEffect(() => {
        if (hasPaginationChange) {
            fetchSerials();
            setHasPaginationChange(false);
        }
    }, [hasPaginationChange]);

    const performSearch = () => {
        setOffset(0);
        fetchSerials();
    };

    const handleFilterKeyDown = (e: any) => {
        if (e.key === "Enter") {
            performSearch();
        }
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
        setHasPaginationChange(true);
    };

    const toggleSerial = (serial: ISerial) => {
        setSelectedSerials((prev) => {
            const next = new Map(prev);
            if (next.has(serial.id)) {
                next.delete(serial.id);
            } else {
                if (maxSerials && next.size >= remainingSlots) {
                    return prev;
                }
                next.set(serial.id, serial);
            }
            return next;
        });
    };

    const toggleSelectAllOnPage = () => {
        setSelectedSerials((prev) => {
            const next = new Map(prev);
            const allOnPageSelected = serials.length > 0 && serials.every((s) => prev.has(s.id));
            if (allOnPageSelected) {
                for (const s of serials) {
                    next.delete(s.id);
                }
            } else {
                for (const s of serials) {
                    if (next.has(s.id)) continue;
                    if (maxSerials && next.size >= remainingSlots) break;
                    next.set(s.id, s);
                }
            }
            return next;
        });
    };

    const removeSerial = (serialId: number) => {
        setSelectedSerials((prev) => {
            const next = new Map(prev);
            next.delete(serialId);
            return next;
        });
    };

    const handleSave = async () => {
        if (selectedSerials.size === 0) return;

        try {
            setIsSaving(true);
            setErrorMessage("");

            const dto: IBulkAssignSerialsDTO = {
                orderItemGuid: props.row.guid,
                serialIds: Array.from(selectedSerials.keys()),
            };

            await SerialService.bulkAssignSerials(dto, userModel, supplierModel, vendorModel);
            props.cancel(true);
        } catch (error: any) {
            console.error("Error bulk assigning serials:", error);
            setErrorMessage(error.message || "Error assigning serials");
        } finally {
            setIsSaving(false);
        }
    };

    // Handler for bulk generate tab
    const handleBulkGenerate = async (serialValues: string[]) => {
        try {
            setIsSaving(true);
            setErrorMessage("");

            await SerialService.bulkCreateSerials(
                {
                    catalogItemGuid: props.row.Catalog_Item!.guid,
                    serials: serialValues,
                    vendorSourceId: selectedVendorSource?.id,
                    orderItemGuid: props.row.guid,
                },
                userModel,
                supplierModel,
                vendorModel
            );

            props.cancel(true);
        } catch (error: any) {
            console.error("Error bulk creating serials:", error);
            setErrorMessage(error.message || "Error creating serials");
        } finally {
            setIsSaving(false);
        }
    };

    const allOnPageSelected = serials.length > 0 && serials.every((s) => selectedSerials.has(s.id));

    if (view === "review") {
        const selectedList = Array.from(selectedSerials.values());

        return (
            <div className="BulkAddSerialSidebar p-2 d-flex flex-column" style={{ height: "100%" }}>
                <div className="text-end">
                    <OwButton centerIconClass="fas fa-times" fn={() => props.cancel(false)} className="btn-danger btn-sm" />
                </div>

                <OwTitle title="serial_bulk_add" />
                <hr />

                <div className="mb-3 fw-bold">Review {selectedList.length} serial(s) to assign</div>

                <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                    {selectedList.map((serial) => (
                        <div key={serial.id} className="d-flex justify-content-between align-items-center mb-1 py-1 border-bottom">
                            <span>{serial.value}</span>
                            <button
                                className="btn btn-outline-danger btn-sm me-2"
                                onClick={() => removeSerial(serial.id)}
                                disabled={isSaving}>
                                <i className="fas fa-times" />
                            </button>
                        </div>
                    ))}
                    {selectedList.length === 0 && <div className="text-muted text-center py-3">No serials selected</div>}
                </div>

                {errorMessage && <div className="alert alert-danger py-2 small mt-2">{errorMessage}</div>}

                <div className="d-flex justify-content-end mt-3">
                    <OwButton title="back" fn={() => setView("select")} className="btn-secondary btn-sm me-2" disabled={isSaving} />
                    <OwButton
                        title="save"
                        fn={handleSave}
                        className="btn-primary btn-sm"
                        disabled={isSaving || selectedList.length === 0}
                        active={isSaving}
                        disableOnActive={true}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="BulkAddSerialSidebar p-2 d-flex flex-column" style={{ height: "100%" }}>
            <div className="text-end">
                <OwButton centerIconClass="fas fa-times" fn={() => props.cancel(false)} className="btn-danger btn-sm" />
            </div>

            <OwTitle title="serial_bulk_add" />
            <hr />

            {/* Vendor Source Typeahead */}
            {supplierModel?.has_purchase_orders && (
                <div className="mb-3">
                    <OwLabel title="vendor_source" />
                    <TypeaheadFn
                        lookupFn={lookupVendorSource}
                        availableOptions={vendorSourceOptions}
                        selectedOptionFn={selectVendorSource}
                        selectedOption={selectedVendorSource}
                        valueProp={"guid"}
                        titleProp={"name"}
                        resetTypeahead={resetVendorSource}
                        resetTypeaheadCallbackFn={resetVendorSourceFn}
                        clearSelectionFn={clearVendorSource}
                        emitSelectionSetup={{ emitObject: true }}
                        placeholder={"Search vendor source..."}
                    />
                </div>
            )}

            {/* Tab Bar */}
            <div className="mb-3">
                <div className="btn-group btn-group-sm w-100">
                    <button
                        type="button"
                        className={`btn ${tab === "existing" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("existing")}>
                        <OwLabel title={"select_existing"} noBottomMargin />
                    </button>
                    <button
                        type="button"
                        className={`btn ${tab === "generate" ? "btn-primary" : "btn-outline-primary"}`}
                        onClick={() => setTab("generate")}>
                        <OwLabel title={"generate_new"} noBottomMargin />
                    </button>
                </div>
            </div>

            <hr />

            {/* Select Existing Tab */}
            {tab === "existing" && (
                <>
                    {/* Filter */}
                    <div className="d-flex gap-2 mb-2 align-items-end">
                        <div className="col">
                            <OwLabel title="serial" />
                            <OwFnInput
                                fn={(e: any) => setFilterValue(e.target.value)}
                                name="filterValue"
                                value={filterValue}
                                onKeyDownFn={handleFilterKeyDown}
                                placeholder="Filter by serial..."
                            />
                        </div>
                        <div className="col-auto">
                            <OwButton title="search" fn={performSearch} className="btn-primary btn-sm" />
                        </div>
                    </div>

                    {/* Selection count */}
                    <div className="mb-2 d-flex justify-content-between align-items-center">
                        <span className="fw-bold">
                            {selectedCount} selected
                            {maxSerials ? ` / ${remainingSlots} available` : ""}
                        </span>
                        {isAtMax && (
                            <span className="text-warning small">
                                <i className="fas fa-exclamation-triangle me-1" />
                                Maximum reached
                            </span>
                        )}
                    </div>

                    {/* Select all on page */}
                    <div className="form-check mb-2 border-bottom pb-2">
                        <label className="form-check-label fw-bold cursor-pointer" htmlFor="selectAllOnPage">
                            <input
                                type="checkbox"
                                className="form-check-input"
                                id="selectAllOnPage"
                                checked={allOnPageSelected}
                                onChange={toggleSelectAllOnPage}
                                disabled={isLoading || serials.length === 0}
                            />
                            Select all on page
                        </label>
                    </div>

                    {/* Serial list */}
                    <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                        {isLoading ? (
                            <div className="text-center py-3">
                                <i className="fas fa-spinner fa-spin" />
                            </div>
                        ) : serials.length === 0 ? (
                            <div className="text-muted text-center py-3">No unassigned serials found</div>
                        ) : (
                            serials.map((serial) => {
                                const isChecked = selectedSerials.has(serial.id);
                                const isDisabled = !isChecked && isAtMax;
                                return (
                                    <div key={serial.id} className="d-flex align-items-center justify-content-between form-check mb-1">
                                        <div>
                                            <input
                                                type="checkbox"
                                                className="form-check-input"
                                                id={`serial-${serial.id}`}
                                                checked={isChecked}
                                                disabled={isDisabled}
                                                onChange={() => toggleSerial(serial)}
                                            />
                                            <label
                                                className={`form-check-label ${isDisabled ? "text-muted" : ""}`}
                                                htmlFor={`serial-${serial.id}`}>
                                                {serial.value}
                                            </label>
                                        </div>

                                        {supplierModel?.Supplier_Integration?.has_fastbound && (
                                            <IntegrationsConnectedBar
                                                supplierModel={supplierModel}
                                                showIntegrations={{ fastbound: true }}
                                                fastboundConnected={!!serial.fastbound_item_id}
                                            />
                                        )}
                                    </div>
                                );
                            })
                        )}
                    </div>

                    {/* Pagination */}
                    <PaginationBar
                        offset={offset}
                        limit={limit}
                        totalRecords={totalCount}
                        onPaginationChanged={paginationChanged}
                        hideLimit={false}
                    />

                    {/* Bottom buttons */}
                    <div className="d-flex justify-content-end mt-3">
                        <OwButton
                            title="review"
                            fn={() => setView("review")}
                            className="btn-primary btn-sm me-2"
                            disabled={selectedCount === 0}
                        />
                        <OwButton title="cancel" fn={() => props.cancel(false)} className="btn-danger btn-sm" />
                    </div>
                </>
            )}

            {/* Generate New Tab */}
            {tab === "generate" && (
                <>
                    {errorMessage && <div className="alert alert-danger py-2 small">{errorMessage}</div>}
                    <BulkGenerateSerialsSidebar
                        embedded
                        onAdd={handleBulkGenerate}
                        maxQty={remainingSlots !== Infinity ? remainingSlots : undefined}
                        cancel={() => props.cancel(false)}
                    />
                </>
            )}
        </div>
    );
};
